import type { Message } from '@wa/shared';

type Quote = NonNullable<Message['replyTo']>;

interface Props {
  quote: Quote;
  /** The customer's name, for a quote of something they said. */
  contactName: string | null;
  /** Inside an outbound bubble the block sits on the green; the bar and text shift to match. */
  outbound: boolean;
  /** Scrolls the thread to the original. Absent in the composer, where there is nothing to scroll. */
  onJump?: (messageId: number) => void;
  /** The composer's ✕. Absent inside a bubble. */
  onDismiss?: () => void;
}

/**
 * The quoted message — the phone's reply block: a coloured bar, who said it, the
 * first line of what they said. The same component draws it above a bubble and
 * inside the composer while a reply is being written, so the two never drift.
 */
export function QuoteBlock({ quote, contactName, outbound, onJump, onDismiss }: Props) {
  const who = quote.direction === 1
    ? (quote.agentName ?? 'You')
    : (contactName ?? 'Customer');

  const line = quote.unavailable
    ? 'Message no longer available'
    : quote.body?.split('\n')[0]
      ?? (quote.fileName ? `${quote.fileName}` : quote.type ? `[${quote.type}]` : '');

  const clickable = !!onJump && quote.id !== null && !quote.unavailable;

  const Tag = clickable ? 'button' : 'div';

  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? () => onJump!(quote.id!) : undefined}
      className={[
        'mb-1 flex w-full items-stretch gap-2 overflow-hidden rounded-md text-left',
        outbound ? 'bg-black/10' : 'bg-black/5 dark:bg-white/5',
        clickable ? 'tap cursor-pointer' : '',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={['w-1 shrink-0 self-stretch', quote.direction === 1 ? 'bg-(--color-brand)' : 'bg-(--color-warn)'].join(' ')}
      />
      <span className="min-w-0 flex-1 py-1 pr-1">
        <span className="block truncate text-micro font-semibold">{who}</span>
        <span className={['block truncate text-micro', quote.unavailable ? 'italic opacity-70' : 'opacity-80'].join(' ')}>
          {quote.type && quote.type !== 'text' && !quote.unavailable ? `${iconFor(quote.type)} ` : ''}{line}
        </span>
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); onDismiss(); }}
          aria-label="Cancel reply"
          className="tap grid w-8 shrink-0 place-items-center text-(--color-muted) hover:text-(--color-ink)"
        >
          <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
            <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </Tag>
  );
}

function iconFor(type: string): string {
  switch (type) {
    case 'image': return '📷';
    case 'video': return '🎥';
    case 'audio': return '🎤';
    case 'document': return '📄';
    case 'sticker': return '🩵';
    case 'location': return '📍';
    default: return '';
  }
}
